/**
 * ReportBuilderEditorProvider
 *
 * Owns the docx editor ref + all per-session state (active case +
 * template, module content, drafts, choices) for the Report Builder.
 * Mounted in <Layout/>.
 *
 * The editor engine is docx-editor.dev (@eigenpal/docx-editor-react).
 * Unlike the previous Tiptap instance, a <DocxEditor> cannot be shared
 * live across mounts — so the provider holds the editor *ref* + the
 * loaded document buffer, and col-3 (<ReportSectionEditor/>) lazily
 * renders the (heavy) <DocxEditor> from them. Cross-session persistence
 * is the uploaded WIP .docx blob (draft_docx_download_url), not a live
 * shared view.
 *
 * Lifecycle:
 *   1. Provider mounts at app start (cheap — no docx engine here).
 *   2. <ReportBuilderPage/> calls `activate(caseNumber, templateId)`;
 *      provider resolves template + draft, then loads the WIP .docx
 *      buffer (preferring the saved draft over the bare template).
 *   3. col-3 renders <DocxEditor documentBuffer={buffer} ref={editorRef}/>.
 *   4. Autosave (debounced) persists draftData + uploads the editor's
 *      current .docx; a refresh restores from that blob.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { DocxEditorRef } from "@eigenpal/docx-editor-react";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import { CLIENT_KEYS } from "@constants/clientKeys";
import { getJurisdictionLabel } from "@constants/jurisdictions";

import {
  useClaimEditorLeaseMutation,
  useCompleteResponseMutation,
  useDownloadResponseDocx,
  useSaveDraftMutation,
  useUploadDraftDocxMutation,
} from "@hooks/mutations/use-templates";
import { caseReportTemplateUrl } from "@hooks/queries/use-case-template";
import { useCaseByCaseNumber } from "@hooks/queries/use-cases";
import { useFinalisationState } from "@hooks/queries/use-finalisation-state";
import { useTemplateOutline } from "@hooks/queries/use-template-outline";
import {
  rawDocxBufferQueryKeyPrefix,
  useCheckDraftStatus,
  useSourceSnippets,
  useTemplateById,
  useTemplateRawDocxBuffer,
} from "@hooks/queries/use-templates";

import { setMessage } from "@redux/notification";

import { downloadBlob } from "@utils/downloadBlob";
import { toTitleCase } from "@utils/string";

import {
  CODE_OF_CONDUCT_HEADING_TITLE,
  LIST_OF_DOCUMENTS_HEADING_TITLE,
} from "../constants";
import { buildDocSignature, classifyEditChange } from "./autosave";
import type { DraftConflict } from "./draft-version";
import {
  nextVersion,
  parseDraftConflict,
  shouldSeedVersion,
  versionSeedKey,
  versionToSend,
} from "./draft-version";
import type { EditorLeaseState } from "./editor-lease";
import {
  canEditWithLease,
  editorSessionId,
  leaseBannerMessage,
} from "./editor-lease";
import type { LeaveState } from "./leave-guard";
import { shouldFlushPendingSave, shouldWarnBeforeUnload } from "./leave-guard";
import type { ReportNote } from "./notes";
import { countNotesBySection, flattenNotes, notesForSection } from "./notes";
import {
  type SaveFailureReason,
  type SaveOutcome,
  persistEditorDocx,
  saveFailureMessage,
} from "./persist-docx";
import { buildOutlineHints } from "./qta-insert";
import type { QtaDraftStatus } from "./qta-source";
import { deriveQtaSource } from "./qta-source";
import { createSaveChain } from "./save-queue";
import type { PinnedBuffer } from "./session-buffer";
import { buildSessionKey, resolveSessionBuffer } from "./session-buffer";
import type {
  ModuleChoice,
  ModuleContent,
  ModuleDraft,
  OrderedModule,
  SectionRow,
  SectionStatus,
} from "./utils";
import {
  buildSectionRows,
  getModuleChoices,
  orderedModulesFromTemplate,
  resolveChoiceKey,
  resolveSeedHtml,
  resolveSeedText,
} from "./utils";

const AUTO_SAVE_DEBOUNCE_MS = 1500;

/**
 * How often the editor renews its advisory lease (MDRS-554 / audit RB-8).
 *
 * Deliberately well inside the server's 90s TTL: a render hitch, a slow
 * request or a briefly-suspended tab must not drop the lease and hand the
 * report to the other tab mid-sentence. Three beats fit in one TTL, so two
 * consecutive failures are survivable.
 */
const EDITOR_LEASE_HEARTBEAT_MS = 30_000;

/** The live ProseMirror EditorState from the docx editor ref. Prefers the live
 *  view (`getView().state`, current at onChange time — avoids the one-edit lag
 *  `getState()` has) and falls back to `getState()` if a future fork drops
 *  `getView()`, so autosave keeps arming rather than silently stopping
 *  (MDRS-402/405). */
const liveEditorState = (ref: DocxEditorRef | null | undefined) => {
  const paged = ref?.getEditorRef?.();
  return paged?.getView?.()?.state ?? paged?.getState?.() ?? undefined;
};

// SectionRow / SectionStatus moved to ./utils so the row-building logic can be
// unit-tested in isolation; re-exported here for existing consumers.
export type { SectionRow, SectionStatus };

/** Has the template-outline query *settled* (as opposed to "produced data")?
 *
 *  The editor's auto-insert effect is gated on this. React Query reports
 *  `isLoading === false` for a **disabled** query (a case with no uploaded
 *  template) and for a **failed** one, and both must read as ready: those
 *  cases still need their preamble sections, QtA block and documents table,
 *  inserted via the static fallback anchors. Gating on "has data" instead
 *  would withhold them forever — trading a placement bug for a worse one
 *  (MDRS-509).
 */
export const deriveOutlineReady = (isOutlineLoading: boolean): boolean =>
  !isOutlineLoading;

type ContextValue = {
  // Activation
  isActive: boolean;
  activeCase: string | null;
  activeTemplate: string | null;
  activate: (caseNumber: string, templateId: string) => void;

  // Editor (docx) + loading
  editorRef: React.RefObject<DocxEditorRef | null>;
  documentBuffer: ArrayBuffer | undefined;
  isBufferLoading: boolean;
  bufferError: unknown;
  onEditorChange: () => void;
  /** Force autosave to arm after a programmatic doc edit (e.g. spell-check fix). */
  markEdited: () => void;
  /** True when the editor doc has no text — autosave skips, UI can gate Save. */
  isEditorEmpty: () => boolean;
  isLoading: boolean;
  lastSavedAt: number | null;
  /** Draft response id (for the PDF export endpoint); null until a draft exists. */
  draftId: string | null;

  // Server-side data
  template: ReturnType<typeof useTemplateById>["data"];
  caseData: ReturnType<typeof useCaseByCaseNumber>["data"];
  orderedModules: OrderedModule[];

  // Module state
  moduleContent: Record<string, ModuleContent>;
  choiceByModule: Record<string, string>;
  /** Source data for the Question To Answer module — the raw LOI
   *  questions text (`summaries_from_doc_sorter`) and any saved per-
   *  question answers (`experts_response`). `null` when no QtA module
   *  is present in the active template. Consumed by the editor's QtA
   *  insert helper. */
  qtaSource: {
    moduleName: string;
    rawQuestions: string;
    expertsResponse: string;
  } | null;
  /** Source data for the List-of-Documents-Provided module — the doc-
   *  sorter document schedule (`summaries_from_doc_sorter`) and any saved
   *  expert commentary (`experts_response`). `null` when no LoI module is
   *  present in the active template (or when no document data exists yet).
   *  Consumed by the editor's documents-table insert helper (MDRS-98). */
  documentsSource: {
    moduleName: string;
    summaryText: string;
    expertsResponse: string;
  } | null;
  /** Pre-classified paragraph text from the backend template-outline
   *  endpoint, bucketed by classification. Frontend insertion helpers
   *  read these in priority over local style-based heuristics so the
   *  same per-template knowledge (computed once server-side) drives
   *  heading / signature / closing / chrome detection. `null` while the
   *  outline is still loading, or when the case has no uploaded
   *  template — callers must fall back to local heuristics in those
   *  cases. */
  outlineHints: {
    knownHeadings: Set<string>;
    knownChrome: Set<string>;
    knownClosing: Set<string>;
    knownSignature: Set<string>;
  } | null;
  /** Auto-discovered insertion anchors from the backend outline. The
   *  CoC anchor is the first section heading after the case-context
   *  opening (`Thank you for your referral…`); the QtA anchor is the
   *  last section heading before the signature / closing-phrase block.
   *  Frontend uses these in priority over the hard-coded fallback lists
   *  in ReportSectionEditor — that's how a brand-new template with
   *  novel heading names ("MY BACKGROUND AND EXPERTISE", etc.) gets
   *  the right insertion point without a code change. */
  outlineAnchors: {
    cocAnchor: string | null;
    qtaAnchor: string | null;
  };

  /** False while the template-outline request is still in flight.
   *
   *  The auto-insert effect must wait for it: until it settles `cocAnchor` is
   *  null, so the insert would fall back to the static anchor list and place
   *  the preamble sections in the wrong spot — then autosave would persist
   *  that. On a first-ever load this request is the slowest of the three (the
   *  backend downloads and parses the .docx, then persists it), so the race
   *  was close to guaranteed on a new case (MDRS-509).
   *
   *  "Settled" means settled, not "has data": a template with no detectable
   *  headings resolves to a null anchor, and a case with no template errors —
   *  both must still get their preamble sections via the fallbacks. */
  outlineReady: boolean;

  // Section navigation
  activeSection: string | null;
  setActiveSection: (key: string) => void;
  handleSelectSection: (key: string) => void;
  /**
   * "Show All Notes" — when on, the Notes panel lists every section's notes
   * and the active section is deselected (the user must reselect a section to
   * leave All-Notes). Lives here, not in the panel, because it drives
   * `activeSection`. See `setShowAll`.
   */
  showAll: boolean;
  setShowAll: (on: boolean) => void;
  sectionRows: SectionRow[];
  activeRow: SectionRow | null;
  activeModule: OrderedModule["module"] | null;
  activeChoices: ModuleChoice[] | null;
  /** Notes for the active section ("This section" tab). */
  activeNotes: ReportNote[];
  /** All notes across sections ("All sections" tab). */
  allNotes: ReportNote[];

  // Handlers
  handleChoiceChange: (moduleName: string, newKey: string) => void;
  /**
   * Explicit save. `ok` is true only when the ENTIRE editor state persisted —
   * draft payload AND the .docx blob. A failure does NOT throw (the .docx can
   * fail to serialise or to upload on its own), so callers that must not
   * proceed on a partial save have to check `.ok` (MDRS-545); wrapping the
   * call in try/catch is not enough. `reason` is carried so a caller adding
   * consequence can also restate the cause — the notification slice holds one
   * message, so a caller that only adds consequence destroys the cause.
   */
  handleSave: () => Promise<SaveOutcome>;
  /** True when leaving right now would lose work — unsaved edits, or a save
   *  still in flight (the .docx upload outlives the `saveDraft` mutation, so
   *  `isSaving` is not the same question). Drives both the `beforeunload`
   *  prompt and the in-app navigation guard. */
  hasUnsavedWork: () => boolean;
  /** Capture the document for an imminent in-app navigation, then let the save
   *  finish in the BACKGROUND. Resolves as soon as the .docx bytes are in hand
   *  — local work, no network — so the nav link never goes dead waiting on two
   *  round trips. `ok: false` means the document could not be captured at all,
   *  which is the only case worth stopping the user for. */
  captureForNavigation: () => Promise<{ ok: boolean }>;
  /** Run the debounced autosave NOW instead of waiting out its 1500ms timer,
   *  and cancel that timer. Called on the way out of the editor — the page
   *  hiding, the provider unmounting, <DocxEditor> unmounting as the user
   *  navigates elsewhere in the app (MDRS-555 / audit RB-9). A no-op, resolving
   *  `{ ok: true }`, when there is nothing pending. */
  flushPendingSave: (opts?: {
    /** Suppress the .docx-failure toast — for the unmount flushes, where the
     *  message would land on whatever page replaces this one. */
    quiet?: boolean;
  }) => Promise<SaveOutcome>;
  handleDownload: () => Promise<void>;

  // --- Concurrency (MDRS-554 / audit RB-8) ---------------------------------
  /**
   * Set when the server refused a save because another tab or user got there
   * first. Non-null means autosave has STOPPED — re-firing the same doomed
   * save on a timer would spam the server and keep telling the user the same
   * thing. Cleared only by reloading the server's copy.
   */
  draftConflict: DraftConflict | null;
  /**
   * Discard this tab's document and load the server's. The caller is expected
   * to have offered `downloadConflictCopy` first — this is unrecoverable.
   */
  reloadAfterConflict: () => Promise<void>;
  /**
   * Hand the user their own work as a .docx before they discard it. Without
   * this, the conflict dialog is just a message telling a doctor their edits
   * are gone.
   */
  downloadConflictCopy: () => Promise<boolean>;
  /** Advisory lease state, or null while unknown. */
  editorLease: EditorLeaseState | null;
  /** False when someone else demonstrably holds the lease, or the case is
   *  finalised. Fails OPEN — see `canEditWithLease`. */
  canEdit: boolean;
  /** Sentence for the read-only banner, or null when there's nothing to say. */
  leaseMessage: string | null;
  /** Steal the lease from its current holder. The escape hatch behind the
   *  banner's button; without it a crashed tab locks a doctor out. */
  takeOverEditing: () => Promise<void>;

  // Status
  isSaving: boolean;
  isDownloading: boolean;
};

const ReportBuilderEditorContext = createContext<ContextValue | null>(null);

export const useReportBuilderEditor = (): ContextValue => {
  const ctx = useContext(ReportBuilderEditorContext);
  if (!ctx) {
    throw new Error(
      "useReportBuilderEditor must be used within <ReportBuilderEditorProvider/>"
    );
  }
  return ctx;
};

/** Map the wire shape onto the client's lease view (MDRS-554). Kept adjacent
 *  to the provider rather than in `editor-lease.ts` because it is purely the
 *  snake_case→camelCase boundary, not policy. */
const toLeaseState = (
  wire:
    | {
        held_by_me: boolean;
        holder_is_self: boolean;
        holder_user_id?: string | null;
        holder_name?: string | null;
        expires_at?: string | null;
      }
    | null
    | undefined
): EditorLeaseState | null =>
  wire
    ? {
        heldByMe: wire.held_by_me,
        holderIsSelf: wire.holder_is_self,
        // Normalise `undefined` away at the boundary: an absent holder and a
        // null holder mean the same thing here, and leaving both shapes alive
        // would make every downstream check carry two falsy cases.
        holderUserId: wire.holder_user_id ?? null,
        holderName: wire.holder_name ?? null,
        expiresAt: wire.expires_at ?? null,
      }
    : null;

export const ReportBuilderEditorProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // ── Activation ──────────────────────────────────────────────────────
  // `activate(case, template)` is called by the Report Builder page on
  // mount. We track the active pair so this provider's state survives
  // navigation to sibling tabs (Assessment Prep / Scribe) — it is mounted in
  // <Layout/>, above the router outlet, so it outlives the page that activated
  // it.
  //
  // NOTE (MDRS-555): <ReportSectionEditor> still accepts `variant="drawer"`,
  // but it has exactly ONE mount site today (ReportBuilder/index.tsx). If a
  // floating drawer is ever added, two editors could be mounted against the
  // same `editorRef` at once, and the leave-page flush below — which assumes
  // the column unmounting is the only one — would need revisiting.
  const [activeCase, setActiveCase] = useState<string | null>(null);
  const [activeTemplate, setActiveTemplate] = useState<string | null>(null);

  const activate = useCallback((caseNumber: string, templateId: string) => {
    setActiveCase(caseNumber);
    setActiveTemplate(templateId);
  }, []);

  // ── Server data ─────────────────────────────────────────────────────
  const { data: template, isLoading: isTemplateLoading } = useTemplateById(
    activeTemplate ?? "",
    {
      enabled: !!activeTemplate,
    }
  );
  const {
    data: draftStatus,
    isLoading: isDraftLoading,
    isFetching: isDraftFetching,
    isPaused: isDraftPaused,
    isSuccess: isDraftLoaded,
  } = useCheckDraftStatus({
    templateId: activeTemplate ?? "",
    caseNumber: activeCase ?? "",
    enabled: !!activeTemplate && !!activeCase,
  });
  const { data: caseData, isLoading: isCaseLoading } = useCaseByCaseNumber({
    case_number: activeCase ?? "",
    enabled: !!activeCase,
  });

  // Editor document buffer.
  //
  // Resolution order, so the editor always shows the user's most recent
  // work:
  //   1. Saved WIP draft (`draft_docx_download_url`) — the user has
  //      already edited this report; restore from there.
  //   2. The case's uploaded report template
  //      (`GET /cases/{id}/report-template`) — the .docx the user uploaded
  //      at case creation. We render it as-is, no module substitution,
  //      until the heading-matching pipeline ships (interim — see
  //      [[use-case-uploaded-template]]).
  //   3. The system template's bare .docx — last-resort fallback for
  //      cases without a report_template_path (older cases or seed data).
  //
  // The hook's `draftDocxDownloadUrl` override branch fetches an
  // arbitrary URL directly, which is exactly the shape we need for the
  // case-upload path too. We chain (1) → (2) here; (3) is what the hook
  // does natively when no override is passed.
  // MDRS-503: both of these are now relative API paths to authenticated,
  // owner-scoped proxies rather than signed Azure URLs — `useTemplateRawDocxBuffer`
  // fetches them through `apiClient`. `report_template_url` is gone from the
  // case payload entirely, so the case-template branch builds its path from the
  // case id and only when a template actually exists.
  // MDRS-554: the nonce is the ONE sanctioned way to drop the buffer pin. The
  // pin is deliberately sticky (it is what stops the editor reloading under the
  // caret), which is also why a tab never notices the server moved on — so
  // "reload the latest version" bumps this rather than weakening the pin for
  // every other refetch.
  const [reloadNonce, setReloadNonce] = useState(0);

  const editorBufferOverrideUrl =
    draftStatus?.draft_docx_download_url ??
    (caseData?.id && caseData?.report_template_path
      ? caseReportTemplateUrl(caseData.id)
      : null);
  const {
    data: fetchedDocumentBuffer,
    isLoading: isBufferLoading,
    error: bufferError,
  } = useTemplateRawDocxBuffer(activeTemplate ?? "", {
    enabled: !!activeTemplate && !isDraftLoading && !isCaseLoading,
    draftDocxDownloadUrl: editorBufferOverrideUrl,
    // MDRS-554: the SAME nonce that re-keys the pin below. Both have to move
    // together — re-keying only the pin re-pins the identical cached
    // ArrayBuffer and the editor never reloads; re-fetching only the bytes
    // leaves the old pin in place. See `rawDocxBufferQueryKey`.
    reloadKey: reloadNonce,
  });

  // ── Stable editor buffer ────────────────────────────────────────────
  // <DocxEditor> performs a FULL document reload whenever its
  // `documentBuffer` prop changes identity — it calls resetForNewDocument()
  // + history.reset(), which wipes the live ProseMirror state, the caret
  // selection, and DOM focus. That is correct for a genuinely new document,
  // but harmful mid-session: once the user is editing, the editor itself is
  // the source of truth. React Query legitimately hands us a NEW buffer after
  // the first autosave — the draft row gets created, so `editorBufferOverrideUrl`
  // flips template→draft and the buffer query re-fetches the just-saved blob —
  // which would reload the editor out from under the user: the caret jumps and
  // focus is lost (reported as "I changed the font and now I can't type"), and
  // any edit made since the autosave snapshot is silently discarded.
  //
  // Pin the buffer to the FIRST one resolved for the current (case, template)
  // session and keep handing that same reference to the editor. A real document
  // switch (different case or template) resets the pin so the new doc loads.
  // Logic lives in `resolveSessionBuffer` (pure + unit-tested).
  //
  // Uses the "adjust state during render" pattern: resolveSessionBuffer returns
  // the SAME reference when nothing changed, so `next !== pinnedBuffer` is false
  // in the steady state (no re-render loop); the setter only fires on the first
  // buffer of a session or on a session switch.
  const sessionKey = buildSessionKey(activeCase, activeTemplate, reloadNonce);
  const [pinnedBuffer, setPinnedBuffer] = useState<PinnedBuffer | null>(null);
  const nextPinnedBuffer = resolveSessionBuffer(
    pinnedBuffer,
    sessionKey,
    fetchedDocumentBuffer
  );
  if (nextPinnedBuffer !== pinnedBuffer) {
    setPinnedBuffer(nextPinnedBuffer);
  }
  const documentBuffer = nextPinnedBuffer?.buffer;

  // ── Draft concurrency (MDRS-554 / audit RB-8) ───────────────────────
  // The token this tab believes is current. A ref, not state: `runSave`
  // threads it between two awaits within a single tick, and a re-render
  // between them would hand the second write a stale closure value — which is
  // the tab conflicting with itself on every autosave.
  const draftVersionRef = useRef<number | null>(null);
  const [draftConflict, setDraftConflict] = useState<DraftConflict | null>(null);
  // Mirrors the state so `runSave` can bail without being re-created (and
  // re-arming the autosave effect) every time the conflict flag flips.
  // Written alongside every `setDraftConflict` rather than during render: an
  // in-flight save has to see the flag before the next render commits, and the
  // two writers are the only places the flag ever moves.
  const draftConflictRef = useRef<DraftConflict | null>(null);

  const { mutateAsync: saveDraft, isPending: isSaving } = useSaveDraftMutation({
    onError: (error) => {
      // A version conflict is handled by `runSave` (which stops autosave and
      // opens the dialog). Raising the generic toast here as well would stack
      // a second, vaguer message on top of the specific one.
      if (parseDraftConflict(error)) return;
      console.error("Failed to save draft:", error);
      // Show the backend's actual reason (the mutation throws it as the Error
      // message — e.g. the finalise-lock or owner-only detail), not a generic
      // string. Fall back only when there's no message.
      dispatch(
        setMessage({
          type: "error",
          content: error?.message || "Failed to save draft.",
        })
      );
    },
  });
  const { mutateAsync: uploadDraftDocx } = useUploadDraftDocxMutation();
  const downloadDocx = useDownloadResponseDocx();
  // MDRS-336: `handleDownload` needs to know whether the case is locked so it
  // can skip the pre-download save (every write 409s once finalised). Same
  // query key as ReportBuilder/index.tsx, so React Query dedupes it rather
  // than issuing a second request.
  // `useFinalisationState` already ANDs `!!responseId` into its own `enabled`,
  // so no explicit guard is needed here.
  const finalisationState = useFinalisationState(
    (draftStatus?.draft_id as string | null) ?? null
  );
  const isFinalised = !!finalisationState.data?.finalised_at;

  // ── Advisory editor lease (MDRS-554 / audit RB-8) ───────────────────
  const responseId = (draftStatus?.draft_id as string | null) ?? null;
  const { mutateAsync: claimEditorLease } = useClaimEditorLeaseMutation();
  const [editorLease, setEditorLease] = useState<EditorLeaseState | null>(null);
  // Previous value of `heldByMe`, so a false->true transition can be detected.
  // `null` until the first claim resolves, which is deliberately NOT a
  // transition — a tab that holds the lease from the outset has nothing stale
  // to reload.
  const leaseHeldByMeRef = useRef<boolean | null>(null);
  // Pin the stable bits of the mutation result so the `handleDownload`
  // useCallback doesn't churn each render — same pattern as the PDF
  // download wiring in ReportBuilder/index.tsx.
  const downloadDocxMutateAsync = downloadDocx.mutateAsync;
  const { mutateAsync: completeResponse } = useCompleteResponseMutation({
    onError: (error) => console.error("Failed to complete response:", error),
  });

  // ── Editor ref + activity tracking ──────────────────────────────────
  const editorRef = useRef<DocxEditorRef | null>(null);
  // Flags whether the user has made ≥1 edit, so the initial draft seed
  // doesn't trigger an autosave round-trip.
  const hasUserEditedRef = useRef(false);
  // Bumped on every editor change so the debounced autosave effect (which
  // can't key on moduleContent — the docx editor doesn't mirror into it)
  // actually re-runs and reschedules.
  const [editTick, setEditTick] = useState(0);
  // Signature of the document content as of the last save. The docx editor
  // fires onChange for NON-content mutations too — `setZoom` (called on every
  // ResizeObserver tick / column toggle), selection, layout/pagination — and
  // each of those used to mark the doc dirty and re-save, which kept resetting
  // lastSavedAt so the label was stuck on "just now". Compare the content
  // signature so only real edits count as a change.
  const lastSavedSigRef = useRef<string | null>(null);
  const docSignature = useCallback(
    (): string =>
      // Read the LIVE view (`getView().state.doc`), NOT `getState().doc`.
      // `getState()` returns a snapshot that syncs a beat AFTER onChange fires,
      // so it lagged one edit behind — a single formatting action (bold) was
      // seen as "no change", and only the NEXT action registered the previous
      // one (MDRS-405). `getView().state` is the live PM state, current at
      // onChange time (the fork reads it the same way for headings). Fall back to
      // `getState()` if a future fork drops `getView()` — a lagging signature is
      // far better than "" (which would make classifyEditChange always ignore, so
      // autosave would silently never arm). (MDRS-402 review.)
      buildDocSignature(liveEditorState(editorRef.current)?.doc),
    []
  );
  // Monotonic count of user edits (MDRS-555 review). `editTick` is state and
  // so can't be read from inside an in-flight save; this ref can, which is what
  // lets a completing save tell "nothing changed while I ran" from "the user
  // kept typing". Bumped from every place that sets `hasUserEditedRef`.
  const editSeqRef = useRef(0);
  const onEditorChange = useCallback(() => {
    // classifyEditChange distinguishes the post-load baseline + non-content
    // mutations (zoom/selection/layout) from real edits — see ./autosave.
    const outcome = classifyEditChange(docSignature(), lastSavedSigRef.current);
    if (outcome.kind === "baseline") {
      lastSavedSigRef.current = outcome.signature;
      return;
    }
    if (outcome.kind === "ignore") return;
    hasUserEditedRef.current = true;
    editSeqRef.current += 1;
    setEditTick((t) => t + 1);
  }, [docSignature]);
  // Force autosave to arm for a programmatic edit we KNOW changed the doc (e.g.
  // a spell-check click-to-fix), WITHOUT re-classifying via docSignature. The
  // correction is dispatched straight to the PM view, which the signature
  // compare can miss, so trust the caller instead of the heuristic (MDRS-405).
  const markEdited = useCallback(() => {
    hasUserEditedRef.current = true;
    editSeqRef.current += 1;
    setEditTick((t) => t + 1);
  }, []);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Whether the docx editor's document has no text. Read straight off the
  // ProseMirror view (the ref exposes no isEmpty()). Used to skip autosave
  // when the doc is blank — so an empty editor never overwrites a real saved
  // draft — and surfaced to the UI to gate Save/Download.
  const isEditorEmpty = useCallback(() => {
    // Live view (see docSignature), with the same getState() fallback.
    const state = liveEditorState(editorRef.current);
    if (!state) return true;
    return state.doc.textContent.trim().length === 0;
  }, []);

  // ── Notes (snippets) — /source-snippets, the col-2 data source. ─────
  const { data: sourceSnippets } = useSourceSnippets({
    templateId: activeTemplate ?? "",
    caseNumber: activeCase ?? "",
    enabled: !!activeTemplate && !!activeCase,
  });
  const allNotes = useMemo<ReportNote[]>(
    () => flattenNotes(sourceSnippets ?? []),
    [sourceSnippets]
  );
  const noteCountBySection = useMemo(
    () => countNotesBySection(allNotes),
    [allNotes]
  );

  // ── Module state ────────────────────────────────────────────────────
  const [moduleContent, setModuleContent] = useState<Record<string, ModuleContent>>(
    {}
  );
  const moduleContentRef = useRef(moduleContent);
  useEffect(() => {
    moduleContentRef.current = moduleContent;
  }, [moduleContent]);

  const [choiceByModule, setChoiceByModule] = useState<Record<string, string>>({});
  const choiceByModuleRef = useRef(choiceByModule);
  useEffect(() => {
    choiceByModuleRef.current = choiceByModule;
  }, [choiceByModule]);

  const acceptedFromServerRef = useRef<Set<string>>(new Set());

  const orderedModules = useMemo(
    () => orderedModulesFromTemplate(template),
    [template]
  );

  // ── Initial seed ────────────────────────────────────────────────────
  // Editor content comes from the .docx buffer (template or saved WIP);
  // here we only seed per-module text/html + choices from the draft so
  // the section list, choices, and draft payload stay populated. No
  // editor.setContent — the <DocxEditor> mounts from `documentBuffer`.
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (hasInitializedRef.current) return;
    if (!activeTemplate || !activeCase) return;
    if (isTemplateLoading || isDraftLoading) return;
    if (!template) return;

    hasInitializedRef.current = true;
    const draftData = (draftStatus?.draft_data ?? {}) as Record<
      string,
      ModuleDraft & { is_accepted?: boolean }
    >;

    const initial: Record<string, ModuleContent> = {};
    const initialChoices: Record<string, string> = {};
    const accepted = new Set<string>();
    for (const { module } of orderedModules) {
      const draft = draftData[module.name];
      const text = resolveSeedText(module, draft);
      const html = resolveSeedHtml(module, draft);
      initial[module.name] = {
        name: module.name,
        label: toTitleCase(module.name),
        text,
        html,
      };
      const choices = getModuleChoices(module);
      if (choices) {
        initialChoices[module.name] = resolveChoiceKey(choices, draft?.value);
      }
      if (draft?.is_accepted) accepted.add(module.name);
    }
    acceptedFromServerRef.current = accepted;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setModuleContent(initial);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setChoiceByModule(initialChoices);
  }, [
    isTemplateLoading,
    isDraftLoading,
    template,
    activeTemplate,
    activeCase,
    draftStatus,
    orderedModules,
  ]);

  // ── Derived section rows. ───────────────────────────────────────────
  // Filtering + numbering live in buildSectionRows (./utils, unit-tested):
  // auto-inserted preamble/LOI sections are always hidden; every remaining
  // (authorable) section is shown regardless of mapped-note count. Note count
  // only drives the col-1 badge, not visibility.
  const sectionRows: SectionRow[] = useMemo(() => {
    return buildSectionRows({
      orderedModules,
      moduleContent,
      acceptedModuleNames: acceptedFromServerRef.current,
      noteCountBySection,
    });
  }, [moduleContent, orderedModules, noteCountBySection]);

  // ── Active section + Show All Notes ─────────────────────────────────
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [showAll, setShowAllState] = useState(false);

  // Keep the active section pointing at a visible row — but NOT while All-Notes
  // is on, where a null activeSection is the intended state (MDRS-8).
  // Auto-picks on first load, and re-picks if the current section drops out of
  // the visible list (e.g. its last mapped note was removed, so it no longer
  // qualifies under MDRS-63).
  useEffect(() => {
    if (showAll) return;
    if (sectionRows.length === 0) return;
    if (activeSection && sectionRows.some((s) => s.key === activeSection)) {
      return;
    }
    const firstDraft = sectionRows.find((s) => s.status === "draft");
    setActiveSection(firstDraft?.key ?? sectionRows[0].key);
  }, [showAll, activeSection, sectionRows]);

  // Turning All-Notes on deselects the active section; the user reselects a
  // section (which turns All-Notes back off) to return to the section view.
  const setShowAll = useCallback((on: boolean) => {
    setShowAllState(on);
    if (on) setActiveSection(null);
  }, []);

  // Everything that makes up "this tab's unsaved module answers".
  //
  // Shared by the two paths that must forget them — switching report, and
  // recovering from a save conflict — so neither can drift into clearing a
  // different subset. A conflict reload that cleared only the .docx would
  // leave `draft_data` holding the loser's answers, and the next save would
  // write them over the winner's with a freshly valid token: the exact
  // overwrite this ticket exists to stop, surviving its own recovery path.
  const clearModuleSessionState = useCallback(() => {
    hasInitializedRef.current = false;
    setModuleContent({});
    setChoiceByModule({});
    acceptedFromServerRef.current = new Set();
  }, []);

  // ── Reset on (case, template) change ────────────────────────────────
  // Switching to a different report tears down our cached module state so
  // the seed effect can re-init from the new template's modules + draft.
  const lastActivationRef = useRef<string | null>(null);
  useEffect(() => {
    const key =
      activeCase && activeTemplate ? `${activeCase}|${activeTemplate}` : null;
    if (lastActivationRef.current === key) return;
    const isFirstActivation = lastActivationRef.current === null;
    lastActivationRef.current = key;
    if (isFirstActivation) return;
    clearModuleSessionState();
    // A different report is a different version lineage. The provider lives in
    // <Layout/> and survives navigation, so without this the new report
    // inherits the previous one's token and its first autosave is refused —
    // a blocking "someone else saved this report" dialog on a report nobody
    // else has touched. `versionSeedKey` then re-seeds from the new report's
    // /draft-status. Any dialog still open from the old report goes too.
    draftVersionRef.current = null;
    draftConflictRef.current = null;
    setDraftConflict(null);
    // A different report also resets the VIEW. A conflict reload deliberately
    // does not — the user is still on the same report and should keep their
    // place in it.
    setActiveSection(null);
    setShowAllState(false);
  }, [activeCase, activeTemplate, clearModuleSessionState]);

  const activeRow = activeSection
    ? (sectionRows.find((s) => s.key === activeSection) ?? null)
    : null;

  const activeModule = useMemo(() => {
    if (!activeSection) return null;
    return (
      orderedModules.find(({ module }) => module.name === activeSection)?.module ??
      null
    );
  }, [activeSection, orderedModules]);

  const activeChoices: ModuleChoice[] | null = useMemo(
    () => (activeModule ? getModuleChoices(activeModule) : null),
    [activeModule]
  );

  const handleChoiceChange = useCallback(
    (moduleName: string, newKey: string) => {
      setChoiceByModule((prev) => ({ ...prev, [moduleName]: newKey }));
      const m = orderedModules.find((om) => om.module.name === moduleName);
      if (!m) return;
      const choices = getModuleChoices(m.module);
      if (!choices) return;
      const choice = choices.find((c) => c.key === newKey);
      if (!choice) return;
      const description = choice.description || choice.display_name;
      // Mirror the chosen boilerplate into module state (drives the draft
      // payload). Inserting it into the docx editor doc happens in col-3
      // (choice → editor slot insert), tracked in the migration plan §3.1.4.
      hasUserEditedRef.current = true;
      editSeqRef.current += 1;
      setModuleContent((prev) => {
        const existing = prev[moduleName];
        const label = toTitleCase(moduleName);
        return {
          ...prev,
          [moduleName]: {
            name: moduleName,
            label,
            text: description,
            html: existing?.html ?? "",
          },
        };
      });
    },
    [orderedModules]
  );

  // Section selection no longer scrolls the (HTML) editor; the docx editor
  // is a paginated surface. Selecting a section just drives col-1/col-2.
  const handleSelectSection = (name: string) => {
    // Selecting a section always returns to the section view.
    setShowAllState(false);
    setActiveSection(name);
  };

  // ── Auto-pick Code of Conduct choice from case jurisdiction ──────────
  // The case carries `jurisdiction` (set at case creation). The CoC module
  // is hidden from the sections list, so the user can't pick a jurisdiction
  // here — instead we resolve the matching CoC choice as soon as both the
  // jurisdiction and the module are available. The chosen text then flows
  // through moduleContent into the editor via applyModuleContent.
  //
  // We only fire if the saved draft has no `value` recorded for the CoC
  // module — the seed populates choiceByModule with a fallback (first
  // choice) when no draft exists, so we can't trust that to gate us;
  // checking the raw draft_data tells us whether a real selection (auto
  // or manual) is already persisted.
  const caseJurisdiction = caseData?.jurisdiction ?? null;
  const autoChoiceFiredFor = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!caseJurisdiction) return;
    if (!hasInitializedRef.current) return;
    const draftData = (draftStatus?.draft_data ?? {}) as Record<string, ModuleDraft>;
    const slug = caseJurisdiction.toLowerCase();
    const label = getJurisdictionLabel(slug).toLowerCase();
    for (const { module } of orderedModules) {
      if (!CODE_OF_CONDUCT_HEADING_TITLE.includes(module.name)) continue;
      if (autoChoiceFiredFor.current.has(module.name)) continue;
      if (draftData[module.name]?.value?.trim()) {
        // Persisted choice exists — respect it. Mark fired so we don't
        // re-check on every render.
        autoChoiceFiredFor.current.add(module.name);
        continue;
      }
      const choices = getModuleChoices(module);
      if (!choices) continue;
      const matched = choices.find((c) => {
        const k = c.key.toLowerCase();
        const dn = c.display_name.toLowerCase();
        return k === slug || (!!label && (k === label || dn === label));
      });
      if (!matched) continue;
      autoChoiceFiredFor.current.add(module.name);
      handleChoiceChange(module.name, matched.key);
    }
  }, [caseJurisdiction, orderedModules, draftStatus, handleChoiceChange]);

  // Reset the auto-choice gate when switching cases.
  useEffect(() => {
    autoChoiceFiredFor.current = new Set();
  }, [activeCase, activeTemplate]);

  // ── Active section notes (mapped snippets for the active section). ──
  const activeNotes = useMemo<ReportNote[]>(
    () => notesForSection(allNotes, activeSection),
    [allNotes, activeSection]
  );

  // ── Save / generate / preview ───────────────────────────────────────
  const buildDraftPayload = useCallback(() => {
    const payload: Record<string, unknown> = {};
    for (const { module } of orderedModules) {
      const m = moduleContentRef.current[module.name];
      if (!m) continue;
      const choiceValue = choiceByModuleRef.current[module.name] ?? null;
      payload[module.name] = {
        value: choiceValue,
        summaries_from_doc_sorter: null,
        summaries_from_assessment_scribe: null,
        experts_response: m.text || null,
        experts_response_html: m.html || null,
        generated_collective_response: null,
        original_experts_response: null,
        is_accepted: !!m.text.trim(),
      };
    }
    return payload as Record<
      string,
      Parameters<typeof saveDraft>[0]["draftData"][string]
    >;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedModules]);

  // Quiet (no-toast) save used by both autosave and handleSave. Persists
  // the draft payload, then (when `uploadDocx` is true) uploads the editor's
  // current .docx as the WIP blob so a refresh restores the user's direct-
  // editor edits. The .docx upload failing must not poison the draft save.
  //
  // `uploadDocx` is false for the on-mount seed save. The seed save exists
  // only to create the DRAFT response row that /source-snippets keys off —
  // uploading the editor buffer at that moment would replace the case's
  // uploaded report_template_url with the editor's round-tripped copy, and
  // any header/footer/media that the editor's serializer can't perfectly
  // round-trip would be lost on the next refresh (the saved blob takes
  // priority over the case template). Real user edits still trigger
  // autosave with uploadDocx=true.
  const runSaveInner = useCallback(
    async ({
      uploadDocx = true,
      serialiseDocx,
      quiet = false,
    }: {
      uploadDocx?: boolean;
      /**
       * Where the .docx bytes come from. Defaults to serialising the LIVE
       * editor, which is right for every save that happens while the user is
       * sitting in the report.
       *
       * The leave-the-page flush overrides it (MDRS-555 / audit RB-9). This
       * function serialises only AFTER awaiting the draft write, and by then
       * <DocxEditor> may be gone — the provider is mounted in <Layout/> and
       * survives navigation, the editor does not. `editorRef.current` is then
       * null, `persistEditorDocx` reports `editor-not-ready`, and the text
       * payload lands while the DOCUMENT doesn't: the next load shows the
       * older version. The flush captures the bytes before it awaits anything
       * and hands the started promise in here.
       */
      serialiseDocx?: () => Promise<ArrayBuffer | null> | undefined;
      /** Suppress the .docx-failure toast. Set by the unmount flushes: this
       *  provider is being torn down (navigation, logout), so the message would
       *  land on whatever replaces it — a save-failed toast on the login
       *  screen. The RETURN value still reports the failure. */
      quiet?: boolean;
    } = {}) => {
      // The edit count as this save begins. Anything the user types from here
      // on is NOT in the payload built below, so it must not be marked saved
      // (MDRS-555 review).
      const editSeqAtStart = editSeqRef.current;
      // No active template/case: nothing was saved. Reported as an explicit
      // failure so callers that gate on success (MDRS-545: finalise) can't
      // read "nothing happened" as "it worked".
      if (!activeTemplate || !activeCase) {
        return { ok: false, reason: "no-case" } as const;
      }
      // MDRS-554: once this tab is known to be behind, every further save is
      // doomed and would re-raise the same refusal. Stay stopped until the
      // user resolves the conflict.
      if (draftConflictRef.current) {
        return { ok: false, reason: "conflict" } as const;
      }
      // Start serialising the .docx NOW, before the draft write rather than
      // after it (MDRS-555). The bytes come from the live ProseMirror view, so
      // every millisecond between "save started" and "bytes captured" is a
      // window in which the user can navigate away, the editor can be torn
      // down, and the document can be lost — the vendor's `save()` builds its
      // buffer, dispatches to the destroyed view, and its catch-all returns
      // `null`, discarding what it just built. The upload still happens in the
      // same order as before (after the draft write, carrying the version that
      // write produced); only the SERIALISATION moves earlier.
      const docxBytes =
        uploadDocx && !serialiseDocx
          ? (() => {
              try {
                const started = editorRef.current?.save();
                started?.catch(() => undefined);
                return started;
              } catch (error) {
                return Promise.reject(error) as Promise<ArrayBuffer | null>;
              }
            })()
          : undefined;
      docxBytes?.catch(() => undefined);
      const payload = buildDraftPayload();
      let saved: { draft_version?: number | null } | undefined;
      try {
        saved = (await saveDraft({
          templateId: activeTemplate,
          caseNumber: activeCase,
          draftData: payload,
          expectedVersion: versionToSend(draftVersionRef.current),
        })) as { draft_version?: number | null } | undefined;
      } catch (error) {
        const conflict = parseDraftConflict(error);
        if (!conflict) throw error;
        // Adopt the server's version so a later reload-and-retry starts from
        // the truth rather than from the stale number that was just refused.
        draftVersionRef.current = conflict.currentVersion;
        setDraftConflict(conflict);
        draftConflictRef.current = conflict;
        return { ok: false, reason: "conflict" } as const;
      }
      // Thread the version the FIRST write produced into the second. Reusing
      // the pre-save value here would 409 against our own save every tick.
      draftVersionRef.current = nextVersion(draftVersionRef.current, saved);
      for (const name of Object.keys(payload)) {
        if (moduleContentRef.current[name]?.text?.trim()) {
          acceptedFromServerRef.current.add(name);
        } else {
          acceptedFromServerRef.current.delete(name);
        }
      }
      // One source of truth. Keeping a separate boolean alongside this would
      // encode the same fact twice and force a fallback reason that, if it
      // ever became reachable, would name the WRONG cause in a message whose
      // entire job is naming the cause.
      let failureReason: SaveFailureReason | undefined;
      if (uploadDocx) {
        // Persist the editor's .docx blob on every save. `save()` preserves
        // embedded media: repack copies existing `word/media/*` verbatim and
        // writes newly-pasted `data:` images into `word/media/` with their
        // relationships (processNewImages). MDRS-218: the previous media-skip
        // workaround here dated from an older build whose save() dropped image
        // binaries — it's now obsolete AND was itself the data-loss bug, since
        // autosave showed "Autosaved" while silently skipping the upload for
        // any doc containing an image. Uploading unconditionally is what keeps
        // body edits + images from being lost on refresh.
        // A .docx that doesn't persist means the editor's exact content —
        // inserted images and direct formatting included — did NOT save, even
        // though the text payload above did. The old behaviour swallowed that
        // in a bare console.warn while still advancing the UI to "Autosaved",
        // so an ingress/CORS or network failure silently dropped images and
        // read as clean data loss. Surface it, and skip the saved-state stamp
        // below so the indicator doesn't lie.
        //
        // MDRS-545: `save()` returning null counts as failure too. It does so
        // when the editor isn't mounted yet (lazy-loaded behind Suspense while
        // the Finalise button is already live) and when the serializer throws
        // internally — and the previous `if (docx) { upload }` let both fall
        // through as success, which finalise would then act on.
        const persisted = await persistEditorDocx(
          serialiseDocx ?? (() => docxBytes ?? editorRef.current?.save()),
          async (docx) => {
            const result = await uploadDraftDocx({
              templateId: activeTemplate,
              caseNumber: activeCase,
              docx,
              expectedVersion: versionToSend(draftVersionRef.current),
            });
            draftVersionRef.current = nextVersion(draftVersionRef.current, result);
            return result;
          }
        );
        // MDRS-554: a conflict here is NOT an ordinary upload failure. The
        // generic "check your connection" advice would be wrong (the
        // connection is fine) and retrying is futile, so it is routed to the
        // dialog instead and autosave stops.
        if (!persisted.ok && persisted.reason === "upload-failed") {
          const conflict = parseDraftConflict(persisted.error);
          if (conflict) {
            draftVersionRef.current = conflict.currentVersion;
            setDraftConflict(conflict);
            draftConflictRef.current = conflict;
            return { ok: false, reason: "conflict" } as const;
          }
        }
        if (!persisted.ok) {
          failureReason = persisted.reason;
          console.warn(
            "[autosave] draft .docx did not persist:",
            persisted.reason,
            persisted.error
          );
          // Only shout when there were genuinely unsaved edits to lose.
          // runSave is shared with the read-only paths (preview, the admin
          // docx download), and `not-serialised` fires there simply because
          // <DocxEditor> is still lazy-loading — nothing was dropped, and the
          // artefact that follows is fine. Same complaint the MDRS-336 comment
          // in handleDownload makes, reached by a new trigger. The RETURN
          // value still reports failure either way; this gates only the toast.
          if (hasUserEditedRef.current && !quiet) {
            dispatch(
              setMessage({
                type: "error",
                content: saveFailureMessage(persisted.reason),
              })
            );
          }
        }
      }
      // Only advance the "Autosaved" state when the full editor snapshot
      // (text payload + .docx blob) persisted. On a .docx failure the text
      // saved but images/formatting did not, so leave the timestamp + dirty
      // flag untouched — a later edit re-arms the autosave and retries.
      // `editSeqRef` moving means the user typed WHILE this save ran. Those
      // keystrokes aren't in what just landed, so clearing the dirty flag (and
      // baselining the signature against the doc as it is NOW, which already
      // contains them) would mark unsaved work saved and the next onChange
      // would read as "no change" — the edit would never be written at all.
      // Leave both alone; the still-set flag re-arms the autosave.
      const editedDuringSave = editSeqRef.current !== editSeqAtStart;
      if (!failureReason && !editedDuringSave) {
        setLastSavedAt(Date.now());
        // Clear the dirty flag now that the edits are persisted. Without this
        // the autosave effect (which depends on `isSaving`) re-arms its timer
        // on every isSaving false→true→false toggle and re-saves forever —
        // the "Saving…"/"Autosaved just now" loop.
        if (!editedDuringSave) {
          hasUserEditedRef.current = false;
          // Snapshot the content so subsequent non-content onChange events
          // (zoom, selection) don't re-mark the doc dirty. Skip if the editor
          // view isn't mounted yet (seed save before the editor parses the
          // buffer) — leave the baseline null so the first real onChange
          // establishes it.
          const sig = docSignature();
          if (sig) lastSavedSigRef.current = sig;
        }
      }
      // These refetches reflect the text-payload save (which DID succeed even
      // when the .docx upload failed) — the DRAFT response row /source-snippets
      // needs, and the `draft_id` the PDF download button keys off — so run
      // them regardless of the .docx outcome.
      void queryClient.invalidateQueries({
        queryKey: [
          CLIENT_KEYS.REPORT_GENERATOR,
          CLIENT_KEYS.TEMPLATES,
          activeTemplate,
          CLIENT_KEYS.CASES,
          activeCase,
          "source-snippets",
        ],
      });
      void queryClient.invalidateQueries({
        queryKey: [
          CLIENT_KEYS.REPORT_GENERATOR,
          CLIENT_KEYS.TEMPLATES,
          activeTemplate,
          CLIENT_KEYS.CASES,
          CLIENT_KEYS.DRAFT_STATUS,
          activeCase,
        ],
      });
      // `ok` only when the entire editor state persisted — callers
      // (handleSave) gate their "Draft saved" confirmation on it so it can't
      // contradict the .docx-failure toast raised above, and finalise gates
      // the whole irreversible operation on it.
      return (
        failureReason ? { ok: false, reason: failureReason } : { ok: true }
      ) as SaveOutcome;
    },
    [
      buildDraftPayload,
      activeCase,
      saveDraft,
      uploadDraftDocx,
      activeTemplate,
      queryClient,
      docSignature,
      dispatch,
    ]
  );

  // ── Serialised saves (MDRS-554 / audit RB-8) ────────────────────────
  // Every save goes through one chain, so two can never be in flight at once.
  //
  // This is not belt-and-braces: `runSave` makes TWO versioned writes per
  // call, and the only previous re-entrancy guard was `isSaving` — the
  // `saveDraft` mutation's `isPending`, which is already false while the .docx
  // upload is still running. A 1500ms autosave tick landing in that window
  // started a second `runSave` carrying the same `expected_version` as the
  // first one's upload, so ONE tab conflicted with ITSELF: the user got a
  // modal blaming "another tab or another person" for their own typing, with
  // "reload and lose your work" as the only way out.
  //
  // Chaining rather than dropping, because the callers have different needs:
  // the autosave tick could safely be dropped, but an explicit Save or a
  // pre-finalise save must actually run. Each waits for the one before it and
  // then reads the version token the predecessor advanced.
  // A ref, not a memo: the chain lives inside `enqueueSave`, so rebuilding it
  // when `runSaveInner` changes identity would let a second chain run beside
  // the first — reintroducing exactly the overlap it exists to prevent.
  const enqueueSaveRef = useRef(createSaveChain<SaveOutcome>({ ok: true }));
  // How many saves are queued or running (MDRS-555 / audit RB-9). `isSaving`
  // can't answer this: it is the `saveDraft` mutation's `isPending`, already
  // false while the .docx upload — runSave's SECOND write — is still open. A
  // tab closed in that window keeps the text and loses the document, so the
  // leave-page prompt has to span the whole of `runSave`, queue time included.
  const saveInFlightRef = useRef(0);
  const runSave = useCallback(
    (
      opts: {
        uploadDocx?: boolean;
        serialiseDocx?: () => Promise<ArrayBuffer | null> | undefined;
        quiet?: boolean;
      } = {}
    ): Promise<SaveOutcome> => {
      // Only saves carrying the user's own edits count. `runSave` is also the
      // on-mount seed save (a brand-new case, before the user has typed a
      // character) and the pre-download save — prompting "you have unsaved
      // work" during either would be asking someone to protect work that does
      // not exist.
      const carriesUserWork = hasUserEditedRef.current;
      if (carriesUserWork) saveInFlightRef.current += 1;
      return enqueueSaveRef
        .current(() => runSaveInner(opts))
        .finally(() => {
          if (carriesUserWork) saveInFlightRef.current -= 1;
        });
    },
    [runSaveInner]
  );

  // ── Conflict recovery (MDRS-554 / audit RB-8) ───────────────────────
  // Hand the user their own work BEFORE they discard it. Without this the
  // conflict dialog is just a message telling a doctor their edits are gone,
  // which is a worse experience than the silent overwrite it replaces — at
  // least that one didn't make them watch.
  const downloadConflictCopy = useCallback(async (): Promise<boolean> => {
    try {
      const docx = await editorRef.current?.save();
      if (!docx || docx.byteLength === 0) return false;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      downloadBlob(
        new Blob([docx], {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        }),
        `report-${activeCase ?? "draft"}-my-copy-${stamp}.docx`
      );
      return true;
    } catch (error) {
      console.error("Failed to export the conflicting draft copy:", error);
      return false;
    }
  }, [activeCase]);

  // Discard this tab's document and load the server's. Bumping the nonce is
  // what actually drops the buffer pin — invalidating the queries alone would
  // refetch the blob and `resolveSessionBuffer` would keep ignoring it, which
  // is the whole reason the pin exists.
  const [isReloading, setIsReloading] = useState(false);
  // The report currently on screen, tracked so an in-flight reload can tell
  // whether it still applies. Synced in an effect rather than during render so
  // it reflects a committed navigation, never a discarded one.
  const activeReportRef = useRef<string | null>(null);
  useEffect(() => {
    activeReportRef.current = `${activeCase ?? ""}|${activeTemplate ?? ""}`;
  }, [activeCase, activeTemplate]);
  const reloadAfterConflict = useCallback(async () => {
    // Which report this reload was started for. The provider is mounted in
    // <Layout/> and survives navigation, so an await here can outlive the
    // report that began it.
    const startedFor = `${activeCase ?? ""}|${activeTemplate ?? ""}`;
    setIsReloading(true);
    try {
      // Pull a fresh /draft-status FIRST and wait for it. `invalidateQueries`
      // only marks the query stale, so the render that bumps the nonce below
      // could still see the pre-conflict `data` React Query holds during a
      // background refetch — and the token would re-seed to the very version
      // this tab was just refused on. `shouldSeedVersion`'s `isFetching` guard
      // covers the same ground; awaiting here removes the race rather than
      // narrowing it.
      if (activeTemplate && activeCase) {
        try {
          await queryClient.refetchQueries({
            queryKey: [
              CLIENT_KEYS.REPORT_GENERATOR,
              CLIENT_KEYS.TEMPLATES,
              activeTemplate,
              CLIENT_KEYS.CASES,
              CLIENT_KEYS.DRAFT_STATUS,
              activeCase,
            ],
          });
        } catch (error) {
          // Offline, say. Press on: the nonce still drops the stale document,
          // and the seed effect will catch up when the query recovers. Blocking
          // the reload would strand the user in the dialog.
          console.warn("[conflict] draft-status refetch failed:", error);
        }
      }
      // Evict the superseded bytes outright. The nonce below already forces a
      // fresh fetch under a new key; this drops the losing document from the
      // cache rather than leaving a stale copy of someone's report sitting in
      // memory for the rest of `gcTime`.
      if (activeTemplate) {
        queryClient.removeQueries({
          queryKey: rawDocxBufferQueryKeyPrefix(activeTemplate),
        });
      }
      setDraftConflict(null);
      draftConflictRef.current = null;
      // Deliberately NOT nulling `draftVersionRef`. `runSaveInner` already set
      // it to the version the server reported in the 409, which is the best
      // number available until the refetch lands — and nulling it would let the
      // seed effect below re-adopt the PRE-conflict version still sitting in the
      // React Query cache (data survives a background refetch), so the very next
      // save would 409 again and discard what the user typed after the reload.
      hasUserEditedRef.current = false;
      lastSavedSigRef.current = null;
      // Discard this tab's module answers as well, not just its document —
      // see `clearModuleSessionState`. Clearing `hasInitializedRef` is what lets
      // the init effect re-seed them from the refetched draft.
      // Bail if the user navigated while the refetch was in flight. Applying
      // report A's reload to report B would clear B's module answers and
      // re-pin B's buffer — and because the init effect cannot depend on a
      // ref, resetting `hasInitializedRef` after B has already seeded does NOT
      // re-run it. B's module state would stay empty and the next autosave
      // would write that emptiness over B's draft: a silent overwrite, which
      // is the whole thing this ticket exists to prevent.
      if (activeReportRef.current !== startedFor) {
        console.warn(
          "[conflict] reload abandoned — the active report changed mid-reload"
        );
        return;
      }
      clearModuleSessionState();
      setReloadNonce((n) => n + 1);
    } finally {
      setIsReloading(false);
    }
  }, [activeCase, activeTemplate, queryClient, clearModuleSessionState]);

  const takeOverEditing = useCallback(async () => {
    if (!responseId) return;
    try {
      const claimed = await claimEditorLease({
        responseId,
        sessionId: editorSessionId(),
        takeover: true,
      });
      leaseHeldByMeRef.current = true;
      setEditorLease(toLeaseState(claimed));
      // This tab has been watching someone else edit, so its pinned document
      // and version token both predate their saves. Without this the user
      // takes control, types, and is met by the conflict dialog seconds later
      // — the lease's own escape hatch leading straight to the thing the
      // lease exists to prevent.
      //
      // Gated on having nothing to lose. `reloadAfterConflict` discards local
      // state, which is exactly right for a tab that has been read-only and
      // exactly wrong for one that has edits: for those the version check
      // still catches the collision and the dialog offers a copy first.
      if (!hasUserEditedRef.current) {
        await reloadAfterConflict();
      }
    } catch (error) {
      console.error("Failed to take over editing:", error);
      dispatch(
        setMessage({
          type: "error",
          content: "Couldn't take over editing. Try again in a moment.",
        })
      );
    }
  }, [responseId, claimEditorLease, dispatch, reloadAfterConflict]);

  // Seed the concurrency token from the server. Only while this tab has no
  // token of its own: once `runSave` is advancing it, the query's value is a
  // snapshot from before our last save and adopting it would 409 us.
  const serverDraftVersion = draftStatus?.draft_version ?? null;
  // Which `reloadNonce` the token was last seeded for. Without it the effect
  // could only ask "is the ref null?", which is false for the whole of normal
  // operation — so a post-conflict reload would never re-seed — and true on
  // the render the reload happens, when the cache still holds the stale
  // pre-conflict number.
  const seededForRef = useRef<string | null>(null);
  const versionSeedIdentity = versionSeedKey(
    activeCase,
    activeTemplate,
    reloadNonce
  );
  useEffect(() => {
    if (
      !shouldSeedVersion({
        isFetching: isDraftFetching,
        isPaused: isDraftPaused,
        hasLoaded: isDraftLoaded,
        seededFor: seededForRef.current,
        seedKey: versionSeedIdentity,
        serverVersion: serverDraftVersion,
      })
    ) {
      return;
    }
    draftVersionRef.current = serverDraftVersion;
    seededForRef.current = versionSeedIdentity;
  }, [
    serverDraftVersion,
    versionSeedIdentity,
    isDraftFetching,
    isDraftPaused,
    isDraftLoaded,
  ]);

  // Claim the lease on mount and renew it on a heartbeat. The interval is well
  // inside the server's TTL so an ordinary render hitch can't drop the lease
  // and hand the report to the other tab mid-sentence.
  useEffect(() => {
    if (!responseId) return;
    let cancelled = false;
    const beat = async () => {
      try {
        // Read per beat, not once: the duplicate-tab probe can re-mint this
        // tab's id shortly after mount (a copied sessionStorage carries the
        // original's id in). A captured value would keep renewing a lease
        // under an id this tab no longer owns, so the real holder and this tab
        // would both believe they hold it — the collision the probe exists to
        // resolve.
        const sessionId = editorSessionId();
        const claimed = await claimEditorLease({ responseId, sessionId });
        if (cancelled) return;
        const next = toLeaseState(claimed);
        // The passive twin of "Take over editing": the holder's lease simply
        // expired and this tab is now the editor. Its pinned document and
        // token still predate their saves, so without a reload the user's
        // first keystroke earns a conflict dialog they did nothing to cause.
        const gainedLease =
          leaseHeldByMeRef.current === false && next?.heldByMe === true;
        leaseHeldByMeRef.current = next?.heldByMe ?? null;
        setEditorLease(next);
        if (gainedLease && !hasUserEditedRef.current) {
          await reloadAfterConflict();
        }
      } catch (error) {
        // Advisory: a lease we can't reach must not disable the editor. The
        // version check is still in force, so the worst case is the user
        // meets a conflict dialog instead of a banner.
        console.warn("[editor-lease] heartbeat failed:", error);
      }
    };
    void beat();
    const timer = setInterval(() => void beat(), EDITOR_LEASE_HEARTBEAT_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [responseId, claimEditorLease, reloadAfterConflict]);

  const canEdit = canEditWithLease(editorLease, {
    isFinalised,
    isReloading,
    hasConflict: !!draftConflict,
  });
  const leaseMessage = leaseBannerMessage(editorLease);

  const handleSave = useCallback(async () => {
    // Explicit Save — force the .docx upload even when the doc has
    // media. The user has chosen to commit this exact state; without
    // the upload, `draft_docx_url` stays null and any subsequent
    // Download-PDF returns 404. The trade-off is that a refresh after
    // an explicit Save may show empty `<img>` boxes for media that
    // the editor's `save()` couldn't fully round-trip, but that's a
    // conscious user action rather than the silent autosave path.
    const outcome = await runSave();
    // Only confirm success when the full save (text + .docx) landed. On a
    // failure runSave already raised the specific error toast, so showing
    // "Draft saved" here too would contradict it.
    if (outcome.ok) {
      dispatch(setMessage({ type: "success", content: "Draft saved." }));
    }
    // MDRS-545: report the outcome rather than swallowing it. A .docx that
    // doesn't persist does NOT throw, so a caller that only try/catches around
    // this sees a clean resolve and assumes the save worked — which is how
    // finalise came to freeze a PDF built from the previous document and then
    // lock the case. Callers that gate on the save (finalise) must check
    // `.ok`, and the reason lets them name the cause in their own message
    // rather than destroying this one (the notification slice holds exactly
    // one message).
    return outcome;
  }, [runSave, dispatch]);

  // ── Auto-save (debounced) ───────────────────────────────────────────
  // Fires AUTO_SAVE_DEBOUNCE_MS after the user stops editing. Gated by
  // hasUserEditedRef so the seed doesn't trigger a save, and skipped when the
  // editor is empty so a blank doc never overwrites a real saved draft.
  //
  // The effect is driven only by edit signals (editTick / choiceByModule) —
  // NOT isSaving — to avoid the re-arm loop a stale design had (each save's
  // isSaving toggle re-ran the effect and re-saved). If a save is already
  // in-flight when the timer fires we reschedule rather than drop, so the
  // edit isn't lost; `isSaving` is read through a ref to stay current without
  // being a dependency.
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSavingRef = useRef(isSaving);
  isSavingRef.current = isSaving;
  useEffect(() => {
    if (!hasUserEditedRef.current) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    const tick = () => {
      // Backpressure only. `isSaving` tracks the saveDraft mutation alone, so
      // it is already false during the .docx upload — it is NOT what keeps two
      // saves apart. `createSaveChain` is (MDRS-554); this just avoids queuing
      // ticks behind a save that is visibly still running.
      if (isSavingRef.current) {
        autoSaveTimerRef.current = setTimeout(tick, AUTO_SAVE_DEBOUNCE_MS);
        return;
      }
      if (isEditorEmpty()) return;
      void runSave();
    };
    autoSaveTimerRef.current = setTimeout(tick, AUTO_SAVE_DEBOUNCE_MS);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [editTick, choiceByModule, isEditorEmpty, runSave]);

  // ── Leaving the page (MDRS-555 / audit RB-9) ────────────────────────
  // At any moment up to AUTO_SAVE_DEBOUNCE_MS of typing exists only in this
  // tab. Three exits used to drop it: navigating elsewhere in the app, hiding
  // or closing the tab, and the provider unmounting on logout (its cleanup
  // above only clears the timer).
  const leaveState = useCallback(
    (): LeaveState => ({
      hasUnsavedEdits: hasUserEditedRef.current,
      isSaveInFlight: saveInFlightRef.current > 0,
      isEditorEmpty: isEditorEmpty(),
      hasActiveReport: !!activeCase && !!activeTemplate,
      hasConflict: !!draftConflictRef.current,
    }),
    [isEditorEmpty, activeCase, activeTemplate]
  );

  // Keyed on the edit count, not merely "a flush is running": an edit made
  // between two leave events is in neither the captured .docx nor the payload
  // the first flush built, so handing that first promise back would report
  // `{ ok: true }` for work that was never saved.
  const pendingFlushRef = useRef<{
    promise: Promise<SaveOutcome>;
    seq: number;
  } | null>(null);

  const flushPendingSave = useCallback(
    ({ quiet = false }: { quiet?: boolean } = {}): Promise<SaveOutcome> => {
      // Cancel the armed tick first, unconditionally. The flush runs the save it
      // was going to run; leaving it alive would save the same state a second
      // time, carrying the version token the flush just advanced past — one tab
      // conflicting with itself, which is the bug MDRS-554 exists to prevent.
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      const pending = pendingFlushRef.current;
      if (pending && pending.seq === editSeqRef.current) return pending.promise;
      if (!shouldFlushPendingSave(leaveState())) {
        return Promise.resolve({ ok: true } as SaveOutcome);
      }
      // Serialise NOW, before anything is awaited and before React has had a
      // chance to tear <DocxEditor> down. `runSave` would otherwise reach the
      // .docx step after the draft write, by which time `editorRef.current` can
      // be null and the document silently wouldn't persist. A sync throw is
      // re-thrown from the thunk so `persistEditorDocx` still classifies it as
      // `serialise-failed` rather than the (transient, retry-worthy)
      // `editor-not-ready`.
      let started: Promise<ArrayBuffer | null> | undefined;
      let serialiseError: unknown;
      try {
        started = editorRef.current?.save();
      } catch (error) {
        serialiseError = error;
      }
      // Mark the promise handled without consuming it: `persistEditorDocx` still
      // awaits `started` and still sees the rejection, but the gap between
      // starting it here and awaiting it after the draft write no longer reads
      // as an unhandled rejection.
      started?.catch(() => undefined);
      const flush = runSave({
        quiet,
        serialiseDocx: () => {
          if (serialiseError) throw serialiseError;
          return started;
        },
      });
      const entry = { promise: flush, seq: editSeqRef.current };
      pendingFlushRef.current = entry;
      // `then(clear, clear)` rather than `finally`, so the derived promise
      // handles a rejection too — `flush` itself is still returned to the caller
      // with its rejection intact. Guarded on identity so a flush that resolves
      // after a LATER one started can't clear the later one's slot.
      const clear = () => {
        if (pendingFlushRef.current === entry) pendingFlushRef.current = null;
      };
      void flush.then(clear, clear);
      return flush;
    },
    [runSave, leaveState]
  );
  const hasUnsavedWork = useCallback(
    () => shouldWarnBeforeUnload(leaveState()),
    [leaveState]
  );

  // The in-app navigation path (MDRS-555). Deliberately NOT `flushPendingSave`:
  // that one awaits the whole save, and holding a route change for two network
  // writes plus a blob upload makes the nav link look broken — it simply does
  // nothing for a second or more, so the user clicks it again.
  //
  // Only the serialisation has to happen before the editor goes; once the bytes
  // exist, the upload can outlive the editor perfectly well, because
  // `serialiseDocx` hands those exact bytes to the save. So: await the capture
  // (local, fast), start the save, and let the navigation proceed.
  //
  // NOT quiet, unlike the unmount flushes. The user will be on another page by
  // the time a failure lands, and that is precisely when they need telling —
  // there is no "Autosaved" indicator in front of them any more.
  const captureForNavigation = useCallback(async (): Promise<{ ok: boolean }> => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    const state = leaveState();
    // A conflict is the one state where the pending edits cannot be rescued by
    // a save at all — `shouldFlushPendingSave` refuses, by design. Reporting
    // `ok` here would walk the user away from unrescuable work without a word,
    // which is exactly what `shouldWarnBeforeUnload` refuses to do for the tab
    // -close path. Same answer for both exits.
    if (state.hasConflict && state.hasUnsavedEdits && !state.isEditorEmpty) {
      return { ok: false };
    }
    if (!shouldFlushPendingSave(state)) return { ok: true };
    let bytes: ArrayBuffer | null = null;
    try {
      bytes = (await editorRef.current?.save()) ?? null;
    } catch {
      bytes = null;
    }
    if (!bytes || bytes.byteLength === 0) return { ok: false };
    const background = runSave({ serialiseDocx: () => Promise.resolve(bytes) });
    background.catch(() => undefined);
    // Register it as THE pending flush for this edit. The editor column
    // unmounts milliseconds later and its cleanup flushes too; without this it
    // would see the dirty flag still set (only a RESOLVED save clears it) and
    // run a second full save against an editor being destroyed — a redundant
    // draft write that bumps `draft_version` for other tabs to conflict
    // against, plus a .docx step that can only fail.
    const entry = { promise: background, seq: editSeqRef.current };
    pendingFlushRef.current = entry;
    const clear = () => {
      if (pendingFlushRef.current === entry) pendingFlushRef.current = null;
    };
    void background.then(clear, clear);
    return { ok: true };
  }, [leaveState, runSave]);

  // The tab-level listeners are registered ONCE (they must not re-bind on every
  // render), so they reach the current flush through a ref.
  const flushPendingSaveRef = useRef(flushPendingSave);
  flushPendingSaveRef.current = flushPendingSave;

  // Flush when the tab goes away. Best-effort by nature: the browser may kill
  // the requests mid-flight, which is why `beforeunload` below asks the user
  // first rather than relying on this. `visibilitychange` as well as
  // `pagehide`, because backgrounding a tab fires only the former — and a
  // backgrounded tab can be discarded without ever coming back.
  useEffect(() => {
    // Fire-and-forget, but not unhandled: `runSave` rejects on a non-conflict
    // save error, and nothing is listening on the way out of the page.
    const flush = () => {
      void flushPendingSaveRef.current().catch(() => undefined);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") flush();
    };
    // Residual gap, deliberately not papered over: these use ordinary
    // `fetch`, which the browser may kill on unload. `beforeunload` covers
    // close/reload by asking first, but a BACKGROUNDED mobile tab that is
    // discarded and never returns gets no prompt and may lose the request.
    // `keepalive` wouldn't rescue it either — the .docx blob is far past the
    // 64KiB keepalive cap, so the document specifically can't be sent that way.
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Flush when the provider itself goes — logout, or the app tearing down.
  // Empty deps so this runs on unmount only, never on a re-render.
  //
  // useLayoutEffect for the same reason as <ReportSectionEditor>'s cleanup: a
  // passive cleanup runs after React has already detached `editorRef`, so the
  // flush would find no editor and quietly save nothing. This provider is an
  // ANCESTOR of <DocxEditor>, and layout cleanups run parent-first within the
  // mutation phase, so the handle is still live here.
  useLayoutEffect(
    () => () => {
      void flushPendingSaveRef.current({ quiet: true }).catch(() => undefined);
    },
    []
  );

  // Ask before the tab closes or reloads with work still unsaved. This is the
  // actual protection — the flushes above are best-effort, and the native
  // prompt is the only thing that can stop the navigation.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!shouldWarnBeforeUnload(leaveState())) return;
      e.preventDefault();
      // Still required by Chrome/Safari to show the prompt, despite being
      // deprecated — same pairing as RecordingHost's session guard.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    // `leaveState` only changes identity when the open report does, so this
    // re-binds on a report switch and never on an ordinary render — cheap
    // enough to read the live values directly instead of through a ref.
  }, [leaveState]);

  // ── Seed-save on mount ──────────────────────────────────────────────
  // /source-snippets returns [] until a DRAFT response row exists; a save
  // creates that row. Fire one quiet save once the seed has run on a
  // fresh case so the Notes panel can populate. Ref-gated to run once.
  // Skip the .docx upload here — the user hasn't edited anything yet, so
  // there's no reason to replace the case's uploaded report_template_url
  // with the editor's round-tripped copy. Doing so on a brand-new case
  // would strip header/footer/media that the editor's serializer can't
  // perfectly re-emit, and the next refresh would load that degraded blob.
  const hasSeedSavedRef = useRef(false);
  useEffect(() => {
    if (hasSeedSavedRef.current) return;
    if (!hasInitializedRef.current) return;
    if (!activeTemplate || !activeCase) return;
    if (draftStatus?.has_draft) {
      hasSeedSavedRef.current = true;
      return;
    }
    hasSeedSavedRef.current = true;
    void runSave({ uploadDocx: false });
  }, [activeTemplate, activeCase, draftStatus, moduleContent, runSave]);

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    try {
      // Make sure the user's most recent edits are persisted (and merged
      // with the case template) on the server, so the .docx we fetch
      // reflects the current editor state, not the previous autosave.
      // handleSave surfaces its own error toast; if it fails we still try
      // the download — the server may have an older draft we can return.
      //
      // MDRS-336: skip the save entirely on a finalised case. The case is
      // locked, so every write 409s — and runSave's own error toast would
      // tell the user the save failed right before handing them a perfectly
      // good file. There are no unsaved edits to flush anyway: the editor is
      // read-only once finalised.
      if (!isFinalised) {
        try {
          await runSave();
        } catch {
          // swallowed — runSave's own error handling already notified the user
        }
      }
      const draftResponseId = draftStatus?.draft_id as string | undefined;
      if (!draftResponseId) {
        dispatch(
          setMessage({
            type: "error",
            content: "Save the draft before downloading.",
          })
        );
        return;
      }
      const stem = caseData?.examinee_name?.replace(/\s+/g, "-") ?? activeCase;
      await downloadDocxMutateAsync({
        responseId: draftResponseId,
        fileName: `${stem}-${template?.name ?? "report"}.docx`,
      });
      void completeResponse;
    } catch (error) {
      console.error("Failed to download .docx:", error);
      dispatch(setMessage({ type: "error", content: "Failed to download .docx." }));
    } finally {
      setIsDownloading(false);
    }
  }, [
    runSave,
    isFinalised,
    draftStatus?.draft_id,
    downloadDocxMutateAsync,
    caseData?.examinee_name,
    activeCase,
    template?.name,
    completeResponse,
    dispatch,
  ]);

  const isLoading =
    !!activeTemplate && (isTemplateLoading || isDraftLoading || isBufferLoading);

  // Derive the QtA source data straight from the loaded template + draft.
  // We don't route this through `moduleContent` because `moduleContent`
  // only carries the user-editable `text` field, but QtA needs both the
  // raw LOI questions (read-only, populated server-side) AND the saved
  // answers, kept separate so the editor's QtA insert can re-stitch them.
  //
  // Lookup is dual-path because of a backend data-keying issue: the
  // background `auto_map_loi_questions_for_case` task creates
  // response_modules via `get_or_create_response_module` without
  // setting `rm.name`, while the frontend's autosave path creates a
  // *separate* response_module with `name = template_module.name`.
  // The draft-status endpoint then keys `draft_data` by `rm.name` —
  // so the named (and empty) autosave row shadows the nameless (and
  // populated) auto-map row. The direct-name lookup misses the data;
  // the fallback scan finds it by content shape. The underlying backend
  // bug is tracked separately — until it's fixed, this scan keeps the
  // QtA insertion working.
  const qtaSource = useMemo<ContextValue["qtaSource"]>(
    () => deriveQtaSource(orderedModules, draftStatus as QtaDraftStatus),
    [orderedModules, draftStatus]
  );

  // Derive the List-of-Documents source data (MDRS-98). Like `qtaSource`,
  // this lives outside `moduleContent` because the doc-sorter summary is
  // read-only data the user can't author through the modules panel —
  // the editor's documents-table insert helper renders it as a 3-column
  // table directly under the SUMMARY OF DOCUMENTS PROVIDED heading.
  const documentsSource = useMemo<ContextValue["documentsSource"]>(() => {
    const docs = orderedModules.find(({ module }) =>
      LIST_OF_DOCUMENTS_HEADING_TITLE.includes(module.name)
    );
    const draftData = (draftStatus?.draft_data ?? {}) as Record<string, ModuleDraft>;
    const draft = docs ? draftData[docs.module.name] : undefined;
    let summaryText = (draft?.summaries_from_doc_sorter ?? "").trim();
    const expertsResponse = (draft?.experts_response ?? "").trim();
    // MDRS-98: templates without a "List of Documents" module still get the
    // documents schedule — the backend derives it from the case's files and
    // returns it as a top-level `documents_summary`. The docs section is always
    // rendered (see ReportSectionEditor), so fall back to that here.
    if (!summaryText) {
      summaryText = (
        (draftStatus as { documents_summary?: string | null } | undefined)
          ?.documents_summary ?? ""
      ).trim();
    }
    if (!summaryText && !expertsResponse) return null;
    return {
      moduleName: docs?.module.name ?? "SUMMARY OF DOCUMENTS PROVIDED",
      summaryText,
      expertsResponse,
    };
  }, [orderedModules, draftStatus]);

  // ── Template outline hints ────────────────────────────────────────────
  // Fetch the backend's pre-computed structural outline for the case's
  // uploaded template. The first request triggers extraction +
  // persistence server-side (see `services/template_outline.py`);
  // subsequent requests hit the cache.
  const { data: outline, isLoading: isOutlineLoading } = useTemplateOutline({
    case_number: activeCase ?? "",
    enabled: !!activeCase,
  });
  const outlineReady = deriveOutlineReady(isOutlineLoading);
  const outlineHints = useMemo<ContextValue["outlineHints"]>(() => {
    if (!outline) return null;
    return buildOutlineHints(outline);
  }, [outline]);
  const outlineAnchors = useMemo<ContextValue["outlineAnchors"]>(
    () => ({
      cocAnchor: outline?.cocAnchor?.text ?? null,
      qtaAnchor: outline?.qtaAnchor?.text ?? null,
    }),
    [outline]
  );

  const value: ContextValue = {
    isActive: !!activeCase && !!activeTemplate,
    activeCase,
    activeTemplate,
    activate,
    editorRef,
    documentBuffer,
    isBufferLoading,
    bufferError,
    onEditorChange,
    markEdited,
    isEditorEmpty,
    isLoading,
    lastSavedAt,
    draftId: (draftStatus?.draft_id as string | null) ?? null,
    template,
    caseData,
    orderedModules,
    moduleContent,
    choiceByModule,
    qtaSource,
    documentsSource,
    outlineHints,
    outlineAnchors,
    outlineReady,
    activeSection,
    setActiveSection,
    handleSelectSection,
    showAll,
    setShowAll,
    sectionRows,
    activeRow,
    activeModule,
    activeChoices,
    activeNotes,
    allNotes,
    handleChoiceChange,
    handleSave,
    hasUnsavedWork,
    captureForNavigation,
    flushPendingSave,
    handleDownload,
    // MDRS-554 / audit RB-8
    draftConflict,
    reloadAfterConflict,
    downloadConflictCopy,
    editorLease,
    canEdit,
    leaseMessage,
    takeOverEditing,
    isSaving,
    isDownloading,
  };

  return (
    <ReportBuilderEditorContext.Provider value={value}>
      {children}
    </ReportBuilderEditorContext.Provider>
  );
};
